/**
 * Workflow Service
 * Frontend service for workflow configuration and operations API calls
 */

import { apiClient, APIResponse } from './client';
import {
  WorkflowConfig,
  WorkflowConfigUpdate,
  TradeWorkflowState,
  TransitionResult,
  ApprovalResult,
  WorkflowHistoryEvent,
  PendingApprovalItem,
  WorkflowState,
} from '../../types/workflow';

class WorkflowService {
  // ============================================
  // Configuration Methods
  // ============================================

  /**
   * Get workflow configuration for a client
   * Returns stored configuration or defaults if none exists
   */
  async getWorkflowConfig(clientId: string): Promise<APIResponse<WorkflowConfig>> {
    return apiClient.get<WorkflowConfig>(`/api/v1/clients/${clientId}/settings/workflow-config`);
  }

  /**
   * Update workflow configuration for a client
   * Requires manage_settings permission
   */
  async updateWorkflowConfig(
    clientId: string,
    config: WorkflowConfigUpdate
  ): Promise<APIResponse<WorkflowConfig>> {
    return apiClient.put<WorkflowConfig>(
      `/api/v1/clients/${clientId}/settings/workflow-config`,
      config
    );
  }

  // ============================================
  // Workflow Operation Methods
  // ============================================

  /**
   * Get current workflow state and valid transitions for a trade
   */
  async getTradeWorkflowState(
    clientId: string,
    tradeId: string
  ): Promise<APIResponse<TradeWorkflowState>> {
    return apiClient.get<TradeWorkflowState>(
      `/api/v1/workflow/${clientId}/trades/${tradeId}/state`
    );
  }

  /**
   * Request a workflow state transition for a trade
   * If approvals required, creates pending approval; otherwise executes immediately
   */
  async requestTransition(
    clientId: string,
    tradeId: string,
    targetState: WorkflowState,
    comment?: string
  ): Promise<APIResponse<TransitionResult>> {
    const params = new URLSearchParams({ targetState });
    if (comment) {
      params.append('comment', comment);
    }
    return apiClient.post<TransitionResult>(
      `/api/v1/workflow/${clientId}/trades/${tradeId}/transition?${params.toString()}`
    );
  }

  /**
   * Approve a pending workflow transition
   * Enforces segregation of duties - requester cannot approve
   */
  async approveTransition(
    clientId: string,
    tradeId: string,
    comment?: string
  ): Promise<APIResponse<ApprovalResult>> {
    const params = comment ? new URLSearchParams({ comment }) : '';
    return apiClient.post<ApprovalResult>(
      `/api/v1/workflow/${clientId}/trades/${tradeId}/approve${params ? `?${params}` : ''}`
    );
  }

  /**
   * Cancel a pending approval request
   */
  async cancelPendingApproval(
    clientId: string,
    tradeId: string,
    reason?: string
  ): Promise<APIResponse<{ success: boolean }>> {
    const params = reason ? new URLSearchParams({ reason }) : '';
    return apiClient.post<{ success: boolean }>(
      `/api/v1/workflow/${clientId}/trades/${tradeId}/cancel-approval${params ? `?${params}` : ''}`
    );
  }

  /**
   * Get workflow history for a trade
   */
  async getWorkflowHistory(
    clientId: string,
    tradeId: string
  ): Promise<APIResponse<WorkflowHistoryEvent[]>> {
    return apiClient.get<WorkflowHistoryEvent[]>(
      `/api/v1/workflow/${clientId}/trades/${tradeId}/history`
    );
  }

  /**
   * Get all pending approvals for a client
   * By default excludes trades requested by the current user (segregation of duties)
   */
  async getPendingApprovals(
    clientId: string,
    excludeOwn: boolean = true
  ): Promise<APIResponse<PendingApprovalItem[]>> {
    const params = new URLSearchParams({ excludeOwn: String(excludeOwn) });
    return apiClient.get<PendingApprovalItem[]>(
      `/api/v1/workflow/${clientId}/pending-approvals?${params.toString()}`
    );
  }
}

// Export singleton instance
export const workflowService = new WorkflowService();
